import React from 'react';
import { Layout, Button, Dropdown, Avatar, Space, Typography, message } from 'antd';
import { 
  UserOutlined, 
  LogoutOutlined, 
  MenuFoldOutlined, 
  MenuUnfoldOutlined,
  SettingOutlined,
  GlobalOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { profileService } from '../../api/profile';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

interface HeaderProps {
  collapsed: boolean;
  onToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ collapsed, onToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  // Language change mutation
  const changeLanguageMutation = useMutation({
    mutationFn: (language: string) => profileService.updateProfile({ interfaceLanguage: language }),
    onSuccess: () => {
      message.success(t('messages.languageChanged'));
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error: Error) => {
      message.error(error?.message || t('messages.errorChangingLanguage'));
    },
  });

  const handleLanguageChange = async (language: string) => {
    i18n.changeLanguage(language);
    await changeLanguageMutation.mutateAsync(language);
  };

  const languageMenuItems = [
    {
      key: 'en',
      label: t('languages.en'),
      onClick: () => handleLanguageChange('en'),
    },
    {
      key: 'ru', 
      label: t('languages.ru'),
      onClick: () => handleLanguageChange('ru'),
    },
    {
      key: 'fa',
      label: t('languages.fa'),
      onClick: () => handleLanguageChange('fa'),
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('header.profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('header.settings'),
      onClick: () => navigate('/settings'),
    },
    {
      key: 'language',
      icon: <GlobalOutlined />,
      label: t('header.language'),
      children: languageMenuItems,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('header.logout'),
      onClick: logout,
    },
  ];

  const getRoleDisplayName = (role: string) => {
    return t(`userRoles.${role}`, role);
  };

  return (
    <AntHeader 
      style={{
        padding: '0 16px',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggle}
          style={{
            fontSize: '16px',
            width: 64,
            height: 64,
          }}
        />
      </div>

      <div>
        <Dropdown 
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          arrow
        >
          <Space style={{ cursor: 'pointer' }}>
            <Avatar icon={<UserOutlined />} />
            <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
              <div style={{ fontWeight: 500, lineHeight: '1' }}>
                {user?.firstName} {user?.lastName}
              </div>
              <Text type="secondary" style={{ fontSize: '12px', lineHeight: '1' }}>
                {user?.role && getRoleDisplayName(user.role)}
              </Text>
            </div>
          </Space>
        </Dropdown>
      </div>
    </AntHeader>
  );
};

export default Header;
